import { Socket } from "socket.io-client";
import {
  ChatEventType,
  ChatMemberEventType,
  ChatWithMembersEventType,
  GroupRequestEventType,
  MeetingEventType,
  MemberEventType,
  MessageEventType,
  P2PChatEventType,
  ParticipantEventType,
  // ReactionEventType,
  ResetCounterEventType,
  RoomFullEventType,
  SocketEventTypes,
  TypingEventType,
  Umbra_ReactionEventType,
  UserEventType,
} from "./types";
import { ConnectionStatus } from "../zustand/ConnectionState";
import { handleMessageEvent } from "./messageEventsHandler";
import { handleResetCounterEvent } from "./resetCounterEventsHandler";
import { handleChatWithMembersEvent } from "./chatWithMembersEventsHandler";
import { handleP2PChatEvent } from "./p2pChatEventsHandler";
import { handleChatEvent } from "./chatEventsHandler";
import { handleChatMemberEvent } from "./chatMemberEventsHandler";
import { handleUserEvent } from "./userEventsHandler";
import { handleMemberEvent } from "./memberEventsHandler";
import { handleGroupRequestEvent } from "./groupRequestEventsHandler";
import {
  // handleReactionEvent,
  umbra_handleReactionEvent,
} from "./reactionEventsHandler";
import {
  handleMeetingEnded,
  handleMeetingStarted,
  handleParticipantJoined,
  handleParticipantLeft,
  handleRoomFull,
} from "./callsEventsHandler";
import { handlePinnedMessageEvent } from "./pinnedMessageEventsHandler";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { ISubscription } from "@/server/types/subscription";
import { queryClient } from "@/queryClient";
import { QUERY_KEYS } from "@/config/queryKeys";
import { safeSocketHandler } from "./safeSocketHandler";
import { fanOutBatched } from "./fanOutBatched";
import { handleTypingEvent } from "./typingEventHandler";
import {
  handleKeyRotatedEvent,
  handleRotationRequestedEvent,
  KeyRotatedEventType,
  RotationRequestedEventType,
} from "./epochEventsHandler";

/**
 * Registers all event handlers for the socket instance
 *
 * All domain event handlers are wrapped with safeSocketHandler to prevent
 * uncaught exceptions from crashing the React tree via the error boundary.
 *
 * @param socketIo - The Socket.IO instance to register handlers for
 * @param setWsConnectionStatus - Function to update the connection status
 */
export const registerSocketEventHandlers = (
  socketIo: Socket,
  setWsConnectionStatus: (status: ConnectionStatus) => void,
): void => {
  socketIo.on("reconnect_attempt", (attempt) => {
    /* eslint-disable no-console */
    console.log(`Reconnection attempt ${attempt}`);
    setWsConnectionStatus(ConnectionStatus.RECONNECTING);
  });

  socketIo.on("reconnect_error", (error) => {
    // Always log connection errors, even in production
    /* eslint-disable no-console */
    console.error("Socket reconnection error:", error);

    // Attempt to recover by updating connection status
    setWsConnectionStatus(ConnectionStatus.RECONNECTING);
  });

  socketIo.on("reconnect_failed", () => {
    /* eslint-disable no-console */
    console.error("Socket reconnection failed after all attempts");
    setWsConnectionStatus(ConnectionStatus.DISCONNECTED);
  });

  // User events
  socketIo.on(
    SocketEventTypes.USER,
    safeSocketHandler("USER", (data: UserEventType) => handleUserEvent(data)),
  );

  // Message events (live: single object; catch-up via fetch-events: array)
  socketIo.on(
    SocketEventTypes.MESSAGE,
    safeSocketHandler(
      "MESSAGE",
      fanOutBatched("MESSAGE", (data: MessageEventType) =>
        handleMessageEvent(data),
      ),
    ),
  );

  // Pinned Message events
  socketIo.on(
    SocketEventTypes.PINNED_MESSAGE,
    safeSocketHandler("PINNED_MESSAGE", (data: MessageEventType) =>
      handlePinnedMessageEvent(data),
    ),
  );

  // Reaction events (live: single object; catch-up via fetch-events: array)
  socketIo.on(
    SocketEventTypes.REACTION,
    safeSocketHandler(
      "REACTION",
      fanOutBatched("REACTION", (data: Umbra_ReactionEventType) =>
        umbra_handleReactionEvent(data),
      ),
    ),
  );

  // Call events
  socketIo.on(
    SocketEventTypes.MEETING_STARTED,
    safeSocketHandler("MEETING_STARTED", async (data: MeetingEventType) => {
      await handleMeetingStarted(data);
    }),
  );
  socketIo.on(
    SocketEventTypes.MEETING_ENDED,
    safeSocketHandler("MEETING_ENDED", async (data: MeetingEventType) => {
      await handleMeetingEnded(data);
    }),
  );
  socketIo.on(
    SocketEventTypes.PARTICIPANT_JOINED,
    safeSocketHandler(
      "PARTICIPANT_JOINED",
      async (data: ParticipantEventType) => {
        await handleParticipantJoined(data);
      },
    ),
  );
  socketIo.on(
    SocketEventTypes.PARTICIPANT_LEFT,
    safeSocketHandler(
      "PARTICIPANT_LEFT",
      async (data: ParticipantEventType) => {
        await handleParticipantLeft(data);
      },
    ),
  );
  socketIo.on(
    SocketEventTypes.ROOM_FULL,
    safeSocketHandler("ROOM_FULL", async (data: RoomFullEventType) => {
      await handleRoomFull(data);
    }),
  );

  // General chat events (live: single object; catch-up via fetch-events: array)
  socketIo.on(
    SocketEventTypes.CHAT,
    safeSocketHandler(
      "CHAT",
      fanOutBatched("CHAT", (data: ChatEventType) => handleChatEvent(data)),
    ),
  );

  // Private chat events
  socketIo.on(
    SocketEventTypes.P2P_CHAT,
    safeSocketHandler("P2P_CHAT", (data: P2PChatEventType) =>
      handleP2PChatEvent(data),
    ),
  );

  // Group chat events
  socketIo.on(
    SocketEventTypes.CHAT_WITH_MEMBERS,
    safeSocketHandler("CHAT_WITH_MEMBERS", (data: ChatWithMembersEventType) =>
      handleChatWithMembersEvent(data),
    ),
  );

  // Chat member events (live: single object; catch-up via fetch-events: array)
  socketIo.on(
    SocketEventTypes.CHAT_MEMBER,
    safeSocketHandler(
      "CHAT_MEMBER",
      fanOutBatched("CHAT_MEMBER", (data: ChatMemberEventType) =>
        handleChatMemberEvent(data),
      ),
    ),
  );

  // Member events (live: single object; catch-up via fetch-events: array)
  socketIo.on(
    SocketEventTypes.MEMBER,
    safeSocketHandler(
      "MEMBER",
      fanOutBatched("MEMBER", (data: MemberEventType) =>
        handleMemberEvent(data),
      ),
    ),
  );

  // Group request events
  socketIo.on(
    SocketEventTypes.GROUP_REQUEST,
    safeSocketHandler("GROUP_REQUEST", (data: GroupRequestEventType) =>
      handleGroupRequestEvent(data),
    ),
  );

  // Reset counter events
  socketIo.on(
    SocketEventTypes.RESET_CHAT_COUNTER,
    safeSocketHandler("RESET_CHAT_COUNTER", (data: ResetCounterEventType) =>
      handleResetCounterEvent(data),
    ),
  );

  // Typing events (ephemeral — never stored, never encrypted)
  socketIo.on(
    SocketEventTypes.TYPING,
    safeSocketHandler(SocketEventTypes.TYPING, (data: TypingEventType) =>
      handleTypingEvent(data),
    ),
  );

  // Subscription events (emitted by backend when Stripe webhook fires)
  socketIo.on(
    SocketEventTypes.SUBSCRIPTION_UPDATED,
    safeSocketHandler("SUBSCRIPTION_UPDATED", (data: ISubscription) => {
      useSubscriptionState.getState().setSubscription(data);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.stripeInvoices,
      });
    }),
  );

  // CSK rotation events
  socketIo.on(
    SocketEventTypes.KEY_ROTATED,
    async (data: KeyRotatedEventType) => {
      await handleKeyRotatedEvent(data);
    },
  );
  socketIo.on(
    SocketEventTypes.ROTATION_REQUESTED,
    async (data: RotationRequestedEventType) => {
      await handleRotationRequestedEvent(data);
    },
  );
};
